import { ApiSystem, Configuration } from "../../state/types";
import { stripUnitSuffix } from "../../utils/systemCode";

const DEFAULT_CLIENT_PROCESS_TEMPLATE = {
      allow_cancel_vehicle: false,
      display_screen_sleep_timer: 60,
      phone_sync: false,
      phone_uc_upload_delay: false,
      actuator_leg_cycle_time: 20,
      use_lighting_controller: false,
      vin_length_err_terminology: "Wrong Identifier Length",
      s3_bucket: "collision-works-d7-dev",
      enabled: {
        0: { name: "WizardVinConfirmation", id: 5 },
        1: { name: "WizardManual", id: 4 },
        2: { name: "WizardExternal", id: 2 },
      },
      location_id: 13,
      cameras: [],
      prefix_removal: 0,
      use_nhtsa_database: false,
      dual_hand_scan_capture: false,
      preferances: {
        vinchar: false,
        vinBypass: false,
        skipExternal: false,
        skipPano: false,
      },
      auto_scan_enabled: false,
      disabled: {
        0: { name: "WizardPano", id: 1 },
        1: { name: "WizardFinal", id: 3 },
        2: { name: "WizardManualPano", id: 6 },
      },
      phone_camera_lens: "wide",
      auto_scan_countdown_length: 3,
      skip_interior_simultaneous: false,
      vin_length: 17,
      actuator_fold_trigger_distance: 300,
      auto_scan_pano: false,
      unique_id: null,
      actuator_leg_stagger_time: 1,
      overlay: null,
      phone_sync_terminology: "SCAN VIN",
      vin_feedback_terminology: "QA2 Confirmed",
      // 🔹 Default/Display flags depend on how many configs exist
      is_default: false,
      is_display_tablet_rooftop: false,
      manual_scan_countdown_length: 3,
      vin_scan_terminology: "QA2",
      url: "https://collision.3iavi.dev",
      ums_environment: "dev",
      overlays: [],
      name: '',
      phone_camera_resolution: "16:9",
      tablet_rooftop_select: false,
      phone_leg_control: false,
      actuator_trigger_count_threshold: 3,
    };

export const findSystemByCode = (systems: any[], code: string | null) => {
  if (!Array.isArray(systems) || !code) return null;
  return systems.find((s: any) => s.systemCode === code) || null;
};

export const buildConfigsList = (system: ApiSystem | null) => {
  if (!system || !system.configurations) return [];
  return Object.entries(system.configurations).map(([name, value]) => ({
    name,
    ...(value as object),
  }));
};

export const getNextUniqueConfigName = (configs: any[]): string => {
  const existingNames = new Set(configs.map((c: any) => c.clientProcess?.name || ""));

  let index = 1;
  let newName = `NewConfig_${index}`;
  while (existingNames.has(newName)) {
    index++;
    newName = `NewConfig_${index}`;
  }
  
  return newName;
};

export const buildNewConfig = (existingConfigs: any[] = []): any => {
  const newConfigName = getNextUniqueConfigName(existingConfigs)

  const isFirstConfig = existingConfigs.length === 0;

  const newConfig = {
    clientProcess: {
      ...DEFAULT_CLIENT_PROCESS_TEMPLATE,
      is_default: isFirstConfig,
      is_display_tablet_rooftop: isFirstConfig,
      name: newConfigName
    },
    assetProcessing: {},
    assetStorage: {},
    assetSyndication: {},
  };

  return newConfig;
};

// src/utils/systemHelpers.ts

/**
 * Syncs config name and clientProcess.name,
 * ensures name uniqueness, and safely renames config if valid.
 */
const syncConfigName = (system: any, currentConfigName: string): any | null => {
  if (!system?.configurations) return system;
  if (!currentConfigName) return { updated: system, newConfigName: currentConfigName}

  const updated = structuredClone(system);
  const configs = Object.entries(updated.configurations || {}) as [string, any];

  const currentEntry = configs.find(([name]) => {
    console.log(name)
    return name === currentConfigName
  });
  if (!currentEntry) return updated;

  const [oldKey, currentConfig] = currentEntry;
  const newName = currentConfig?.clientProcess?.name?.trim?.() || oldKey;

  // 🔹 Prevent duplicate names
  const duplicate = configs.some(
    ([name, cfg]) =>
      name !== oldKey &&
      (cfg?.clientProcess?.name?.trim?.() || name) === newName
  );

  if (duplicate) {
    alert(`A configuration named "${newName}" already exists.`);
    return null;
  }

  // 🔹 Rename key if changed
  if (newName !== oldKey) {
    delete updated.configurations[oldKey];
    updated.configurations[newName] = currentConfig;
    return { updated, newConfigName: newName };
  }

  return { updated, newConfigName: currentConfigName };
};

/**
 * Enforces only one config has clientProcess.is_default = true,
 * and ensures at least one default exists.
 */
const enforceUniqueDefault = (
  system: any,
  currentConfigName: string
): any | null => {
  const updated = structuredClone(system);
  const configs = Object.entries(updated.configurations || {}) as [string, any];
  const current = updated.configurations[currentConfigName];
  const cp = current?.clientProcess || {};

  if (cp.is_default) {
    // turn off all others
    for (const [name, cfg] of configs) {
      if (name !== currentConfigName && cfg?.clientProcess?.is_default) {
        cfg.clientProcess.is_default = false;
      }
    }
  } else {
    const anyDefault = configs.some(
      ([name, cfg]) =>
        name !== currentConfigName && cfg?.clientProcess?.is_default === true
    );
    if (!anyDefault) {
      alert("At least one configuration must be marked as default.");
      return null;
    }
  }

  return updated;
};

/**
 * Enforces only one config has clientProcess.is_display_tablet_rooftop = true,
 * and ensures at least one exists.
 */
const enforceUniqueTabletDefault = (
  system: any,
  currentConfigName: string
): any | null => {
  const updated = structuredClone(system);
  const configs = Object.entries(updated.configurations || {}) as [string, any];
  const current = updated.configurations[currentConfigName];
  const cp = current?.clientProcess || {};

  if (cp.is_display_tablet_rooftop) {
    // turn off all others
    for (const [name, cfg] of configs) {
      if (
        name !== currentConfigName &&
        cfg?.clientProcess?.is_display_tablet_rooftop
      ) {
        cfg.clientProcess.is_display_tablet_rooftop = false;
      }
    }
  } else {
    const anyTablet = configs.some(
      ([name, cfg]) =>
        name !== currentConfigName &&
        cfg?.clientProcess?.is_display_tablet_rooftop === true
    );
    if (!anyTablet) {
      alert("At least one configuration must be set as display tablet rooftop.");
      return null;
    }
  }

  return updated;
};

/**
 * Master method — orchestrates all name/default validation logic.
 */
export const validateCanSaveSystem = (
  system: any,
  currentConfigName: string
): { updated: any; newConfigName: string } | null => {
  if (!system?.configurations) return { updated: system, newConfigName: currentConfigName };

  // 1️⃣ Sync config name and ensure uniqueness
  const renameResult = syncConfigName(system, currentConfigName);
  if (!renameResult) return null;
  let { updated: systemWithRename, newConfigName } = renameResult;

  // 2️⃣ Enforce only one default
  const defaultResult = enforceUniqueDefault(systemWithRename, newConfigName);
  if (!defaultResult) return null;

  // 3️⃣ Enforce only one tablet default
  const tabletResult = enforceUniqueTabletDefault(defaultResult, newConfigName);
  if (!tabletResult) return null;

  // ✅ Return both the updated system and the final name
  return { updated: tabletResult, newConfigName };
};

export const isDuplicateSystemCode = (currentCode: string,  systems: any[]): boolean => {
  if (!currentCode?.trim()) return false;

  return systems.some(
    (s: any) =>
      s.systemCode.toLowerCase() === currentCode.trim().toLowerCase()
  );
};

export const getUniqueConfigName = (existingConfigs: any[], baseName: string) => {
  const existingNames = new Set(existingConfigs.map(c => c?.clientProcess?.name).filter(Boolean));

  // match your current naming behavior
  let candidate = baseName.endsWith("_copy") ? `${baseName}_2` : `${baseName}_copy`;

  if (!existingNames.has(candidate)) return candidate;

  // deterministic-ish uniqueness without Date.now() chaos if you prefer:
  let i = 2;
  while (existingNames.has(`${candidate}_${i}`)) i++;
  return `${candidate}_${i}`;
};

export interface GroupedSystem {
  label: string;
  systems: { code: string; label: string }[];
}

export const GROUP_ORDER = [
  "Car Dealers",
  "Alliance Auto Autions",
  "America's Auto Actions",
  "Dealers Auto Auctions",
  "Manheim Auto Auctions",
  "Metro Auto Auctions",
  "Boats",
  "RV",
  "Internal",
];

export function groupAndFilterSystems(
  systems: ApiSystem[],
  accessibleSystemCodes: string[],
  searchTerm: string
): GroupedSystem[] {
  const groupMap: Record<string, GroupedSystem> = {};
  const otherGroup: GroupedSystem = { label: "Other", systems: [] };

  GROUP_ORDER.forEach(label => (groupMap[label] = { label, systems: [] }));

  const q = searchTerm.trim().toLowerCase();

  accessibleSystemCodes.forEach(code => {
    const system = systems.find(s => s.systemCode === code);
    if (!system) return;

    const name = system.commonName || "Unknown";
    const systemLabel = `${name} (${code})`;

    const isMatch =
      !q ||
      name.toLowerCase().includes(q) ||
      code.toLowerCase().includes(q);

    if (!isMatch) return;

    const category = system.system_category;
    if (category && groupMap[category]) groupMap[category].systems.push({ code, label: systemLabel });
    else otherGroup.systems.push({ code, label: systemLabel });
  });

  Object.values(groupMap).forEach(g => g.systems.sort((a, b) => a.label.localeCompare(b.label)));
  otherGroup.systems.sort((a, b) => a.label.localeCompare(b.label));

  const all = [...GROUP_ORDER.map(label => groupMap[label]), otherGroup];
  return all.filter(g => g.systems.length > 0);
}

export const normalizeSystemsData = (systems: ApiSystem[]): ApiSystem[] => {
  const byCode = new Map<string, ApiSystem>();

  systems.forEach((system) => {
    const baseSystemCode = stripUnitSuffix(system.systemCode);

    // If there are no configurations, there's nothing to fix.
    const normalizedConfigurations: { [key: string]: Configuration } = {};
    if (system.configurations) {
      // Loop over each configuration by its key (e.g., "umstest", "NewConfig_1")
      for (const configKey in system.configurations) {
        // Standard practice to ensure we're not iterating over prototype properties
        if (Object.prototype.hasOwnProperty.call(system.configurations, configKey)) {
          const originalConfig = system.configurations[configKey];

          // ❗ THE CORE LOGIC: Check if clientProcess.name is missing.
          if (!originalConfig.clientProcess?.name) {
            // If it's missing, create a new config object that includes a
            // clientProcess object with the name set to its key.
            normalizedConfigurations[configKey] = {
              ...originalConfig,
              clientProcess: {
                ...(originalConfig.clientProcess || {...DEFAULT_CLIENT_PROCESS_TEMPLATE}), // Preserve other clientProcess fields if they exist
                name: configKey, // Inject the name!
              },
            };
          } else {
            // If the name exists, the config is fine. Just copy it over.
            normalizedConfigurations[configKey] = originalConfig;
          }
        }
      }
    }

    const normalizedSystem: ApiSystem = {
      ...system,
      systemCode: baseSystemCode,
      configurations: normalizedConfigurations,
    };

    if (!byCode.has(baseSystemCode)) {
      byCode.set(baseSystemCode, normalizedSystem);
    }
  });

  return Array.from(byCode.values());
};
